import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { providerActions } from '../../../../store';
import { PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH } from '../constants';
import { updateProviderReviewerBcba } from '../services';

import type { AppDispatch, RootState } from '../../../../store';

export function useProviderReviewerBcba(providerId: number | undefined) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const lastFetchedProvider = useSelector(
    (state: RootState) => state.providers.lastFetchedProvider,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPasswordValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingActive, setPendingActive] = useState(true);

  const setPassword = (value: string) => {
    setPasswordValue(value);
    setError(null);
  };

  const openDialog = (nextActive: boolean) => {
    setPendingActive(nextActive);
    setPasswordValue('');
    setError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPasswordValue('');
    setError(null);
  };

  const submit = async () => {
    if (
      providerId === undefined ||
      !password ||
      password.length < PROVIDER_SSN_REVEAL_PASSWORD_MIN_LENGTH
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateProviderReviewerBcba(
        providerId,
        pendingActive,
        password,
        getAccessTokenSilently,
      );
      dispatch(
        providerActions.updateProviderLists({
          ...updated,
          clinical_status: updated.clinical_status ?? lastFetchedProvider?.clinical_status,
          roles: updated.roles?.length ? updated.roles : lastFetchedProvider?.roles,
        }),
      );
      closeDialog();
    } catch {
      setError('Unable to update reviewer status');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    dialogOpen,
    password,
    setPassword,
    submitting,
    error,
    pendingActive,
    openDialog,
    closeDialog,
    submit,
  };
}
